import { FC, useMemo, useState } from 'react';
import {
  BankFileConversionRail,
  BodyText,
  Box,
  Card,
  FileConversionConfiguration,
  FileConversionService,
  Icon,
  useForm,
  withStyles,
} from 'vopay';
import { Title, CircleButton, Button } from 'UI';
import styles from './ProcessFileDialog.module.scss';
import { useTranslation } from 'react-i18next';
import processFile from '../svgs/process_file.svg';
import { useprocessFileFormFields, type ProcessFileFormValues } from './useProcessFileFormFields';
import useFileConversion from '@/hooks/useApi/useFileConversion';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useDataFetchIndicator } from '@/hooks';
import { useHistory } from 'react-router-dom';
import { DetailsRows } from 'UI';

type ProcessFileDialogProps = {
  onCancel: () => void;
  onConfirm: (values: ProcessFileFormValues) => void;
  fileConversionID: string;
  configurations: FileConversionConfiguration[];
  configurationsLoading: boolean;
  service: FileConversionService;
};

const cn = withStyles(styles);

export type ProcessFileImgs = 'domainSetup';

const ProcessFileDialog: FC<ProcessFileDialogProps> = ({
  onCancel,
  onConfirm,
  fileConversionID,
  configurations,
  configurationsLoading,
  service,
}) => {
  const history = useHistory();
  const { t } = useTranslation('translations', {
    keyPrefix: 'FileUpload.History.FileConversionTable.ProcessFileDialog',
  });
  const { convertTo } = useprocessFileFormFields({ configurations, configurationsLoading });
  const alertDialog = useAlertDialog();
  const { submitFile } = useFileConversion();
  const [, setLoading] = useDataFetchIndicator();

  const initialValues: ProcessFileFormValues = {
    Service: service,
    PaymentRail: '',
    FileConversionConfigurationID: '',
    ValueDate: '',
    AlternatePaymentRail: '',
  };

  const { renderFormElements, ctx } = useForm<ProcessFileFormValues>({
    initialValues,
    config: convertTo,
    onSubmit: (values) => {
      onConfirm({
        ...values,
      });
    },
  });

  // State to store the submitFile response
  const [submitResult, setSubmitResult] = useState<{
    TotalTransactions: number;
    TotalAmount: string;
    TotalFailed: number;
  } | null>(null);

  const handleProcessFile = async () => {
    try {
      setLoading(true);
      if (!ctx.values.PaymentRail) {
        alertDialog.error(
          t('processErrorTitle', { defaultValue: 'Error Processing File' }),
          t('paymentRailRequired', { defaultValue: 'Payment Rail is required.' }),
        );
      }


      const res = await submitFile({
        FileConversionID: fileConversionID,
        ValueDate: ctx.values.ValueDate,
        PaymentRail: ctx.values.PaymentRail as BankFileConversionRail,
        AlternatePaymentRail: ctx.values.AlternatePaymentRail === '' ? undefined : ctx.values.AlternatePaymentRail,
        FileConversionConfigurationID: ctx.values.FileConversionConfigurationID,
      });
      setSubmitResult(res);
    } catch (err) {
      alertDialog.error(
        t('processErrorTitle', { defaultValue: 'Submission Error' }),
        t('processErrorTitle', { defaultValue: 'Submission Error' }),
      );
    } finally {
      setLoading(false);
    }
  };

  const details = useMemo(() => {
    const config = configurations.find((config) => config.FileConversionConfigurationID === ctx.values.FileConversionConfigurationID);
    const arr = [
      { label: t('transferType', { defaultValue: 'Transfer Type' }), value: ctx.values.Service || '' },
      { label: t('paymentRail', { defaultValue: 'Payment Rail' }), value: ctx.values.PaymentRail || '' },
      {
        label: t('alternativePaymentRail', { defaultValue: 'Fallback Payment Rail' }),
        value: ctx.values.AlternatePaymentRail || '',
      },
      { label: t('processor', { defaultValue: 'Processor' }), value: config ? config.FinancialInstitution : '' },
    ];
    if (submitResult) {
      arr.push(
        {
          label: t('numberOfTransactions', { defaultValue: 'Number Of Transactions' }),
          value: String(submitResult.TotalTransactions),
        },
        { label: t('totalAmount', { defaultValue: 'Total Amount' }), value: `$${submitResult.TotalAmount}` },
        { label: t('totalFailed', { defaultValue: 'Total Failed' }), value: String(submitResult.TotalFailed) },
      );
    }
    return arr;
  }, [ctx.values, submitResult, t]);

  return (
    <Card className={cn('modalContainer')}>
      {!submitResult ? (
        <>
          <div className={cn('header')}>
            <img src={processFile} alt={'process file'} className={cn('icon')} />
            <Title level="4">{t('title', { defaultValue: 'Download File' })}</Title>
            <BodyText className={cn('description')}>
              {t('desc', {
                defaultValue: `This will initiate the transaction batch submission. Once confirmed, the file will be processed and can’t be modified.`,
              })}
            </BodyText>
          </div>
          <div className={cn('closeButton')}>
            <CircleButton icon="close" size="large" circleFill onClick={onCancel} />
          </div>
          <Box flx="col" gap="common">
            {renderFormElements({ config: convertTo })}
            <Button type="button" className={cn('button')} colorScheme="primary" onClick={handleProcessFile}>
              {t('process', { defaultValue: 'Process' })}
            </Button>
          </Box>
        </>
      ) : (
        <div className={cn('content')}>
          {/* <Complete title={t('title')} showAmounts={false} details={details} /> */}
          <div className={cn('iconContainer')}>
            <div className={cn('successIcon')}>
              <Icon icon="check" height="34px" width="34px" color="primary-light" />
            </div>
            <Title level="3">{t('title', { defaultValue: 'Download File' })}</Title>
          </div>

          <DetailsRows details={details} />

          <Button
            colorScheme="primary"
            onClick={() => {
              onCancel();
              history.push('/file-upload-history?tab=fileConversion');
            }}
          >
            {t('close', { defaultValue: 'Close' })}
          </Button>
        </div>
      )}
    </Card>
  );
};

export default ProcessFileDialog;
